import {
  Box,
  Heading,
  Image,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Flex,
  useToast,
  SimpleGrid,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiOutlineTrash,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import PartySelectById from "./PartySelectById";
import JournalSelectById from "./JournalSelectById";
import { debounce } from "lodash";

const AccountsEditor = ({ account, onClose, isNew }) => {
  const [accountHeader, setAccountHeader] = useState({});
  const [journals, setJournals] = useState([]);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (account) {
      setAccountHeader({ ...account, journals: undefined });
      setJournals(account.journals || []);
    }
  }, [account]);

  const handleHeaderChange = useMemo(
    () =>
      debounce((field, value) => {
        setAccountHeader((prev) => {
          if (prev[field] === value) return prev;
          return { ...prev, [field]: value };
        });
      }, 5),
    []
  );

  const handleSave = async () => {
    try {
      const fullAccount = { ...accountHeader, journals };
      const response = await fetch(
        isNew
          ? "https://ledgers.poc.mrc.ai/api/accounts"
          : `https://ledgers.poc.mrc.ai/api/accounts/${accountHeader._id}`,
        {
          method: isNew ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fullAccount),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to ${isNew ? "create" : "save"} account. Status: ${
            response.status
          }`
        );
      }

      const result = await response.json();

      toast({
        title: isNew ? "Account created" : "Account saved",
        description: isNew
          ? "New Account was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/accounts/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving Account:", error);

      toast({
        title: "Save failed",
        description: error.message || "An error occurred while saving Account.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    navigate("/Poc");
  };

  // Helper to handle journal value change
  const handleJournalChange = (index, value) => {
    setJournals((prev) =>
      prev.map((journal, i) => (i === index ? value : journal))
    );
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">Edit Account</Heading>
                <Tooltip label="Save Account" aria-label="Save Account Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Tabs orientation="horizontal" variant="line" colorScheme="linkedin">
            <TabList>
              <Tab>Account Details</Tab>
              <Tab>Journals</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <UuidPreview uuid={accountHeader._id} />
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mt={4}>
                    <PartySelectById
                      label="Owner"
                      selectedPartyId={accountHeader.owner}
                      onChange={(value) => handleHeaderChange("owner", value)}
                      role=""
                      size="md"
                    />
                    <CodesetSelect
                      label="Type"
                      codesetCode="SLC002"
                      value={
                        accountHeader.type
                          ? { code: accountHeader.type, description: "" }
                          : null
                      }
                      onChange={(val) => handleHeaderChange("type", val?.code)}
                    />

                    <PartySelectById
                      label="Subject"
                      selectedPartyId={accountHeader.subject}
                      onChange={(value) => handleHeaderChange("subject", value)}
                      role=""
                      size="md"
                    />

                    <FormControl>
                      <FormLabel>Name</FormLabel>
                      <Input
                        value={accountHeader.name || ""}
                        onChange={(e) =>
                          handleHeaderChange("name", e.target.value)
                        }
                      />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid
                    columns={{ base: 1, md: 5 }}
                    spacing={6}
                    mt={4}></SimpleGrid>
                </Box>
              </TabPanel>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <HStack alignItems="center" mb={4}>
                    <Heading size="md">Journals</Heading>
                    <IconButton
                      icon={<HiOutlinePlus />}
                      size="md"
                      onClick={() => setJournals([...journals, ""])}
                      aria-label="Add Journal"
                    />
                  </HStack>
                  <Box overflowX="auto">
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "8px" }}>
                            Journal
                          </th>
                          <th style={{ width: "50px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {journals.map((journal, index) => (
                          <tr key={index}>
                            <td style={{ padding: "8px" }}>
                              <JournalSelectById
                                label=""
                                selectedJournalId={journal.id}
                                onChange={(value) =>
                                  handleJournalChange(index, value)
                                }
                                size="md"
                              />
                            </td>
                            <td style={{ padding: "8px" }}>
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="sm"
                                onClick={() => {
                                  const newJournals = journals.filter(
                                    (_, i) => i !== index
                                  );
                                  setJournals(newJournals);
                                }}
                                aria-label="Remove Journal"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <VStack spacing={6} align="stretch"></VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default AccountsEditor;
